// Step-Up (RFC 9470) via the OAuth2 `web_message` + PKCE flow, per the Identity
// team / oauth2ng PR wix-private/server-infra#65059.
//
// Flow: open oauth2ng's `/authorize` (`response_mode=web_message`, + acr_values,
// PKCE S256); oauth2ng runs step-up, then its `usersWebMessageHtml` page posts
// `{ code, state, error }` to the opener; we validate origin + state and exchange
// the authorization `code` (with the same redirect_uri + PKCE verifier) for an
// access token at the token endpoint.
//
// This is a generic package: `clientId` is **required** (each consumer registers its
// own OAuth client — there is no shared default), and `redirectUri` must be one
// registered for that client (defaults to `window.location.origin`). Endpoints and
// `scope` default to the shared Identity values below and are overridable.
export const STEP_UP_OAUTH_CONFIG = {
  // No default: the OAuth client id is per-consumer and MUST be supplied via config.
  CLIENT_ID: '',
  SCOPE: 'session_bound_access',
  RESPONSE_TYPE: 'code',
  RESPONSE_MODE: 'web_message',
  AUTHORIZE_URL: 'https://users.wix.com/v1/oauth/authorize',
  TOKEN_URL: 'https://users.wix.com/v1/oauth/token',
  // Blank = derive from the current origin (window.location.origin) at flow time;
  // set a value to override. Must be one registered for CLIENT_ID, and identical on
  // /authorize and /token.
  REDIRECT_URI: '',
  WINDOW_NAME: 'wixStepUp',
  // Backstop TTL: if nothing settles the flow, stop listening and close the popup.
  // Last resort — a user who closes the popup is detected much sooner by the poll.
  TIMEOUT_MS: 5 * 60 * 1000,
  // Best-effort poll of `popup.closed` so a user-closed popup settles as cancelled
  // promptly instead of hanging until TIMEOUT_MS (a hang would stall the per-acr
  // single-flight). `window.closed` stays readable cross-origin; TIMEOUT_MS remains
  // a backstop in case an environment neuters the handle.
  CLOSE_CHECK_INTERVAL_MS: 500,
  // How long the hidden prompt=none iframe may take to answer. oauth2ng answers a
  // silent /authorize promptly, so silence means the transport broke.
  SILENT_TIMEOUT_MS: 15 * 1000,
  WIDTH: 600,
  HEIGHT: 530,
} as const;

// OIDC `prompt=none`: the silent /authorize attempt, answered with a code or
// `error=interaction_required` without rendering any UI.
export const STEP_UP_PROMPT_NONE = 'none';

// The silent attempt's answer when the session does not satisfy the requirement and
// the user has to verify interactively.
export const STEP_UP_INTERACTION_REQUIRED = 'interaction_required';

// Applicative error code raised by the identity Step-Up guard (RFC 9470) when the
// caller's authentication context (acr / auth_time) is not strong or fresh enough.
// Arrives as an HTTP 403 whose body has
// `details.applicationError.code === 'STEP_UP_REQUIRED'`.
export const STEP_UP_REQUIRED_CODE = 'STEP_UP_REQUIRED';

// Terminal code we surface when step-up could not be completed (the popup reported a
// failure with no specific code, or a replay still failed). Deliberately NOT
// STEP_UP_REQUIRED, so downstream treats it as final, not "step up again".
export const STEP_UP_FAILED_CODE = 'STEP_UP_FAILED';

// Code for a user-dismissed / timed-out step-up. Distinct so the app can treat
// cancellation quietly while still handling it through the unified path.
export const STEP_UP_CANCELLED_CODE = 'STEP_UP_CANCELLED';

// Code for a step-up popup the BROWSER refused to open (popup blocker / no user
// activation left). Distinct from STEP_UP_FAILED because it is user-recoverable
// (allow popups / retry from a fresh click) and worth dedicated visibility.
export const STEP_UP_POPUP_BLOCKED_CODE = 'STEP_UP_POPUP_BLOCKED';

// Step-up mint lifecycle event names — the shared vocabulary between StepUpManager
// (emitter) and its observers (telemetry, UX). Const-object enum so every consumer
// references the same values instead of scattering string literals.
export const STEP_UP_EVENT_TYPE = {
  MINT_STARTED: 'mint-started',
  MINT_SUCCEEDED: 'mint-succeeded',
  MINT_FAILED: 'mint-failed',
} as const;
export type StepUpEventType =
  (typeof STEP_UP_EVENT_TYPE)[keyof typeof STEP_UP_EVENT_TYPE];

// Why a mint failed. POPUP_BLOCKED is the one failure the USER can fix (allow popups
// / retry from a fresh click); CANCELLED is a deliberate dismissal (quiet); FAILED
// is everything else (OAuth error, token-exchange failure, misconfig).
export const STEP_UP_MINT_FAILURE_REASON = {
  POPUP_BLOCKED: 'popup-blocked',
  CANCELLED: 'cancelled',
  FAILED: 'failed',
} as const;
export type StepUpMintFailureReason =
  (typeof STEP_UP_MINT_FAILURE_REASON)[keyof typeof STEP_UP_MINT_FAILURE_REASON];
